import type { AppDispatch } from "@store/index";
import { datasetApi } from "@store/api/dataset-api";
import type {
  QuestionResponsesSearchFilter,
  QuestionVariablesResponse,
} from "@models/Filter";
import type { VisualizationBase } from "@models/VisualizationBase";
import { visualizationBaseToSearchFilters } from "./visualization-base-utils";

/**
 * Resolves a chart's visualizationBase into /search-ready filters. Fetches
 * (or reuses the RTK Query cache for) each condition's question variables so
 * IDs can be translated to the backend's expected identifiers before calling
 * the pure visualizationBaseToSearchFilters transform.
 */
export async function resolveVisualizationBaseFilters(
  dispatch: AppDispatch,
  datasetId: string | undefined | null,
  base: VisualizationBase | undefined | null,
): Promise<QuestionResponsesSearchFilter[]> {
  if (!datasetId || !base?.enabled || base.conditions.length === 0) return [];

  const uniqueQuestionIds = Array.from(
    new Set(base.conditions.map((c) => c.questionId).filter(Boolean)),
  );

  if (uniqueQuestionIds.length === 0) return [];

  const entries = await Promise.all(
    uniqueQuestionIds.map(async (questionId) => {
      try {
        const result = await dispatch(
          datasetApi.endpoints.getDatasetQuestionVariablesByElement.initiate({
            id: datasetId,
            dataset_element_id: questionId,
          }),
        ).unwrap();
        return [questionId, result] as const;
      } catch {
        return [questionId, undefined] as const;
      }
    }),
  );

  const resolvedVariablesByQuestionId: Record<string, QuestionVariablesResponse> = {};
  entries.forEach(([questionId, result]) => {
    if (result) resolvedVariablesByQuestionId[questionId] = result;
  });

  return visualizationBaseToSearchFilters(base, resolvedVariablesByQuestionId);
}
